import { useEffect, useMemo, useRef, useState } from 'react';
import { Input } from '@finn-auto/design-system/atoms/input';
import { Loader2, Search } from 'lucide-react';
import { cn } from '../../../../shared';
import { searchPeople, type DirectoryPerson } from '../../services/directoryApi';

/**
 * Type-ahead over the Google Workspace directory.
 *
 * Debounced, and every in-flight response is checked against the current query
 * before being rendered — otherwise a slow reply for "an" can land after a fast
 * one for "anna" and overwrite it with stale suggestions.
 */
export function PersonPicker({
  onSelect,
  excludeEmails = [],
  placeholder = 'Start typing a name…',
  allowTypedEmail = true,
  label = 'Add interviewer',
}: {
  onSelect: (person: DirectoryPerson) => void;
  label?: string;
  excludeEmails?: string[];
  placeholder?: string;
  /**
   * Whether a @finn.com address absent from the directory may still be chosen.
   *
   * True for interviewers: someone can be on a loop before the directory knows
   * them, and refusing would block a real link. False where the value is a join
   * key rather than a person — the TA owner is the axis the overview filters on,
   * so a typo there silently hides a link from its own owner.
   */
  allowTypedEmail?: boolean;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<DirectoryPerson[]>([]);
  const [loading, setLoading] = useState(false);
  const [fromDirectory, setFromDirectory] = useState(true);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const latestQuery = useRef('');

  // Keyed on the joined string, not the array: excludeEmails is a fresh array on
  // every parent render, so a Set built from it restarted the debounce constantly.
  // Lowercased: the typed address is lowercased before comparison, and callers
  // pass whatever casing the stored link happens to carry.
  const excludeKey = excludeEmails.map((e) => e.trim().toLowerCase()).join(',');
  const excluded = useMemo(() => new Set(excludeKey.split(',').filter(Boolean)), [excludeKey]);

  // Every state change happens inside the timer callback, never synchronously in
  // the effect body — the latter causes cascading renders.
  useEffect(() => {
    const q = query.trim();
    latestQuery.current = q;
    if (q.length < 2) return;

    const timer = window.setTimeout(() => {
      setLoading(true);
      void searchPeople(q)
        .then((res) => {
          // Ignore a reply that no longer matches what is typed.
          if (latestQuery.current !== q) return;
          setResults(res.people.filter((p) => !excluded.has(p.email)));
          setFromDirectory(res.fromDirectory);
          setHighlight(0);
        })
        .finally(() => {
          // Unconditional: gating on the query still matching left the spinner
          // spinning forever whenever the user typed during a request.
          setLoading(false);
        });
    }, 220);

    return () => window.clearTimeout(timer);
  }, [query, excludeKey, excluded]);

  useEffect(() => {
    if (!open) return;
    const onClickAway = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, [open]);

  function choose(person: DirectoryPerson) {
    onSelect(person);
    setQuery('');
    setResults([]);
    setOpen(false);
  }

  // Derived, not stored: a query under two characters shows nothing without an
  // effect having to clear `results`.
  const suggestions = query.trim().length < 2 ? [] : results;

  /** A typed-out address that isn't in the directory, where the caller allows it. */
  const typedEmail = query.trim().toLowerCase();
  const canUseTyped =
    allowTypedEmail &&
    /^[^\s@,;]+@finn\.com$/.test(typedEmail) &&
    // Checked against the exclusion list, not only against what is on screen:
    // `results` is already filtered by `excluded`, so an address that is excluded
    // is guaranteed absent from `suggestions` — making that check alone always
    // pass and offering to add someone who is on the panel already.
    !excluded.has(typedEmail) &&
    !suggestions.some((r) => r.email === typedEmail);

  return (
    <div ref={boxRef} className="relative min-w-[260px] flex-1">
      <span className="mb-1 block text-xs font-medium text-gray-700">{label}</span>
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <Input
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            const options = canUseTyped ? suggestions.length + 1 : suggestions.length;
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setHighlight((h) => Math.min(h + 1, Math.max(options - 1, 0)));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setHighlight((h) => Math.max(h - 1, 0));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              if (highlight < suggestions.length) {
                if (suggestions[highlight]) choose(suggestions[highlight]);
              } else if (canUseTyped) {
                choose({ email: typedEmail, name: null });
              }
            } else if (e.key === 'Escape') {
              setOpen(false);
            }
          }}
          placeholder={placeholder}
          className="pl-8"
        />
        {loading ? (
          <Loader2 className="absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-gray-400" />
        ) : null}
      </div>

      {open && query.trim().length >= 2 ? (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
          {suggestions.map((person, idx) => (
            <button
              key={person.email}
              type="button"
              onMouseEnter={() => setHighlight(idx)}
              /**
               * mousedown, not click — the pattern ChannelPicker in this repo
               * already documents ("it fires before the input's blur").
               *
               * With `onClick`, the mousedown that precedes it moves focus off
               * the search input first, and the row could be gone before the
               * click landed: a recruiter saw their own name and photo in the
               * list and could not select it, ending up typing the whole
               * address by hand (Theresa, Sep 2026). preventDefault keeps the
               * focus on the input so the list is never torn down mid-gesture.
               */
              onMouseDown={(e) => {
                e.preventDefault();
                choose(person);
              }}
              className={cn(
                'flex w-full items-center gap-2.5 px-3 py-2 text-left',
                idx === highlight ? 'bg-gray-100' : 'bg-white'
              )}
            >
              {person.photoUrl ? (
                <img src={person.photoUrl} alt="" className="h-6 w-6 rounded-full" />
              ) : (
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-200 text-[10px] font-semibold text-gray-600">
                  {(person.name || person.email).slice(0, 2).toUpperCase()}
                </span>
              )}
              <span className="min-w-0">
                <span className="block truncate text-sm text-gray-900">
                  {person.name || person.email}
                </span>
                <span className="block truncate text-xs text-gray-500">
                  {person.title ? `${person.title} · ${person.email}` : person.email}
                </span>
              </span>
            </button>
          ))}

          {canUseTyped ? (
            <button
              type="button"
              onMouseEnter={() => setHighlight(suggestions.length)}
              // Same reason as the directory rows above.
              onMouseDown={(e) => {
                e.preventDefault();
                choose({ email: typedEmail, name: null });
              }}
              className={cn(
                'w-full px-3 py-2 text-left text-sm',
                highlight === suggestions.length ? 'bg-gray-100' : 'bg-white'
              )}
            >
              Add <span className="font-medium">{typedEmail}</span>
            </button>
          ) : null}

          {!loading && suggestions.length === 0 && !canUseTyped ? (
            <p className="px-3 py-2 text-xs text-gray-500">No matches.</p>
          ) : null}

          {!fromDirectory && suggestions.length > 0 ? (
            <p className="border-t border-gray-100 bg-[#FCEEE2] px-3 py-1.5 text-[11px] text-[#854F0B]">
              Google directory unavailable — showing only people who have signed into Spaces.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
